import React from 'react';
import PropTypes from 'prop-types';

import { IconSpinner, ListingCard } from '../../components';
import { propTypes } from '../../util/types';

import css from '../HandymanPage/HandymanPage.module.css';

const LISTING_RENDER_SIZES = [
  '(max-width: 767px) 100vw',
  '(max-width: 1023px) 50vw',
  '33vw',
].join(', ');

/**
 * Shared listings section used by CleaningPage, CleaningCityPage, and
 * CleaningTorontoServicePage. Mirrors HandymanListingsSection but with
 * cleaner-facing labels in the loading / error / empty / teaser states.
 */
const CleaningListingsSection = props => {
  const {
    eyebrow,
    heading,
    text,
    listings,
    queryInProgress,
    queryListingsError,
    browseAllPath,
    emptyStateChildren,
  } = props;

  const primaryListings = listings.slice(0, 9);
  const teaserListings = listings.slice(9, 12);

  let body;
  if (queryInProgress) {
    body = (
      <div className={css.listingsState}>
        <IconSpinner className={css.loader} />
      </div>
    );
  } else if (queryListingsError) {
    body = (
      <div className={css.listingsState}>
        <h3 className={css.cardTitle}>Live listings are unavailable right now</h3>
        <p className={css.cardText}>
          You can still open the full listings page to browse active cleaning listings directly.
        </p>
        <a className={css.secondaryButton} href={browseAllPath} rel="nofollow">
          Browse all listings
        </a>
      </div>
    );
  } else if (listings.length > 0) {
    body = (
      <>
        <div className={css.listingGrid}>
          {primaryListings.map(listing => (
            <ListingCard
              className={css.marketplaceListingCard}
              key={listing.id.uuid}
              listing={listing}
              renderSizes={LISTING_RENDER_SIZES}
            />
          ))}
        </div>

        {teaserListings.length > 0 ? (
          <div className={css.teaserWrap}>
            <div className={css.teaserGrid}>
              {teaserListings.map(listing => (
                <div className={css.teaserCardShell} key={listing.id.uuid}>
                  <ListingCard
                    className={css.marketplaceListingCard}
                    listing={listing}
                    renderSizes={LISTING_RENDER_SIZES}
                  />
                </div>
              ))}
            </div>

            <div className={css.teaserCta}>
              <h3 className={css.teaserTitle}>Need more options?</h3>
              <p className={css.teaserText}>
                Open the full listings page to browse more active cleaning listings on FixitTask.
              </p>
              <a className={css.primaryButton} href={browseAllPath} rel="nofollow">
                See more cleaners
              </a>
            </div>
          </div>
        ) : (
          <div className={css.inlineCta}>
            <a className={css.primaryButton} href={browseAllPath} rel="nofollow">
              See more cleaners
            </a>
          </div>
        )}
      </>
    );
  } else {
    body = emptyStateChildren || (
      <div className={css.listingsState}>
        <h3 className={css.cardTitle}>No live listings yet</h3>
        <p className={css.cardText}>
          Open the full listings page to explore active providers on FixitTask.
        </p>
        <a className={css.secondaryButton} href={browseAllPath} rel="nofollow">
          Browse all listings
        </a>
      </div>
    );
  }

  return (
    <section className={css.section}>
      <div className={css.sectionHeader}>
        {eyebrow ? <p className={css.sectionEyebrow}>{eyebrow}</p> : null}
        <h2 className={css.sectionTitle}>{heading}</h2>
        {text ? <p className={css.sectionText}>{text}</p> : null}
      </div>
      {body}
    </section>
  );
};

const { arrayOf, bool, node, string } = PropTypes;

CleaningListingsSection.defaultProps = {
  eyebrow: 'Live marketplace listings',
  text: null,
  listings: [],
  queryInProgress: false,
  queryListingsError: null,
  emptyStateChildren: null,
};

CleaningListingsSection.propTypes = {
  eyebrow: string,
  heading: string.isRequired,
  text: string,
  listings: arrayOf(propTypes.listing),
  queryInProgress: bool,
  queryListingsError: propTypes.error,
  browseAllPath: string.isRequired,
  emptyStateChildren: node,
};

export default CleaningListingsSection;
